import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  latestClosedBusinessDay,
  resolvePeriod,
  type PeriodPresetDefinition,
  type RelativePeriodUnit,
} from '@ozon-ai/periods';
import {
  PERIOD_PRESET_TYPE_LABELS,
  PERIOD_UNIT_LABELS,
  type PeriodPreset,
  type UserPreferences,
} from '@ozon-ai/user/contracts';
import { Button } from '@/components/ui/button.tsx';
import { api } from '../../../api/client';
import { DateField } from '../../../ui/DateField';
import { formatPeriodRange } from '../../../components/period-picker/format-period';
import { SETTINGS_ACCESS } from '../settings-paths';
import {
  Field,
  SelectField,
  SettingsErrorAlert,
  SettingsFormCard,
  SettingsHint,
  SettingsSubPageShell,
} from '../settings-shared';

type PresetType = PeriodPresetDefinition['type'];

interface Draft {
  id?: string;
  name: string;
  type: PresetType;
  from: string;
  to: string;
  amount: string;
  unit: RelativePeriodUnit;
}

function emptyDraft(anchor: string): Draft {
  return {
    name: '',
    type: 'relative',
    from: anchor,
    to: anchor,
    amount: '30',
    unit: 'day',
  };
}

function draftFromPreset(preset: PeriodPreset, anchor: string): Draft {
  const base = emptyDraft(anchor);
  if (preset.definition.type === 'absolute') {
    return {
      ...base,
      id: preset.id,
      name: preset.name,
      type: 'absolute',
      from: preset.definition.from,
      to: preset.definition.to,
    };
  }
  if (preset.definition.type === 'relative') {
    return {
      ...base,
      id: preset.id,
      name: preset.name,
      type: 'relative',
      amount: String(preset.definition.amount),
      unit: preset.definition.unit,
    };
  }
  return {
    ...base,
    id: preset.id,
    name: preset.name,
    type: 'tracking',
    from: preset.definition.from,
  };
}

function buildDefinition(draft: Draft): PeriodPresetDefinition {
  if (draft.type === 'absolute') {
    return { type: 'absolute', from: draft.from, to: draft.to };
  }
  if (draft.type === 'tracking') {
    return { type: 'tracking', from: draft.from };
  }
  return { type: 'relative', amount: Number(draft.amount), unit: draft.unit };
}

export function SettingsPeriodsPage() {
  const anchor = useMemo(() => latestClosedBusinessDay(), []);
  const [presets, setPresets] = useState<PeriodPreset[]>([]);
  const [primaryId, setPrimaryId] = useState<string | undefined>();
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(latestClosedBusinessDay()));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextPresets, preferences] = await Promise.all([
        api.get<PeriodPreset[]>('/period-presets'),
        api.get<UserPreferences>('/user/preferences'),
      ]);
      setPresets(nextPresets);
      setPrimaryId(preferences.primaryPeriodPresetId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить периоды');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const preview = useMemo(() => {
    try {
      const range = resolvePeriod({ type: 'preset', preset: buildDefinition(draft) }, anchor);
      return formatPeriodRange(range.from, range.to);
    } catch (err) {
      return err instanceof Error ? err.message : '';
    }
  }, [anchor, draft]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const definition = buildDefinition(draft);
      if (draft.id) {
        await api.put<PeriodPreset>('/period-presets', {
          id: draft.id,
          name: draft.name,
          definition,
        });
      } else {
        await api.post<PeriodPreset>('/period-presets', { name: draft.name, definition });
      }
      setDraft(emptyDraft(anchor));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить период');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.delete<PeriodPreset>(`/period-presets?id=${encodeURIComponent(id)}`);
      if (draft.id === id) setDraft(emptyDraft(anchor));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось удалить период');
    } finally {
      setBusy(false);
    }
  }

  async function makePrimary(id: string) {
    setBusy(true);
    setError(null);
    try {
      const saved = await api.put<UserPreferences>('/user/preferences', { primaryPeriodPresetId: id });
      setPrimaryId(saved.primaryPeriodPresetId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось выбрать основной период');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsSubPageShell title="Периоды" backTo={SETTINGS_ACCESS}>
      <SettingsHint>
        Личные периоды для аналитики. Основной период появляется в выборе на страницах расчётов.
        Последний доступный день — вчера по Москве.
      </SettingsHint>

      {error ? <SettingsErrorAlert>{error}</SettingsErrorAlert> : null}

      <SettingsFormCard title={draft.id ? 'Изменить период' : 'Новый период'}>
        <div className="flex flex-col gap-3">
          <Field
            id="period-name"
            label="Название"
            value={draft.name}
            onChange={(name) => setDraft((current) => ({ ...current, name }))}
          />
          <SelectField
            id="period-type"
            label="Тип"
            value={draft.type}
            onChange={(type) => setDraft((current) => ({ ...current, type }))}
            options={(Object.keys(PERIOD_PRESET_TYPE_LABELS) as PresetType[]).map((type) => ({
              value: type,
              label: PERIOD_PRESET_TYPE_LABELS[type],
            }))}
          />

          {draft.type === 'absolute' ? (
            <div className="flex flex-wrap gap-3">
              <DateField
                id="preset-from"
                label="С"
                value={draft.from}
                onChange={(from) => setDraft((current) => ({ ...current, from }))}
                max={draft.to < anchor ? draft.to : anchor}
              />
              <DateField
                id="preset-to"
                label="По"
                value={draft.to}
                onChange={(to) => setDraft((current) => ({ ...current, to }))}
                min={draft.from}
                max={anchor}
              />
            </div>
          ) : null}

          {draft.type === 'relative' ? (
            <div className="flex flex-wrap gap-3">
              <Field
                id="preset-amount"
                label="Количество"
                type="number"
                value={draft.amount}
                onChange={(amount) => setDraft((current) => ({ ...current, amount }))}
              />
              <SelectField
                id="period-unit"
                className="min-w-[10rem] flex-1"
                label="Единица"
                value={draft.unit}
                onChange={(unit) => setDraft((current) => ({ ...current, unit }))}
                options={(Object.keys(PERIOD_UNIT_LABELS) as RelativePeriodUnit[]).map((unit) => ({
                  value: unit,
                  label: PERIOD_UNIT_LABELS[unit],
                }))}
              />
            </div>
          ) : null}

          {draft.type === 'tracking' ? (
            <DateField
              id="preset-tracking-from"
              label="С"
              value={draft.from}
              onChange={(from) => setDraft((current) => ({ ...current, from }))}
              max={anchor}
            />
          ) : null}

          {preview ? <SettingsHint>{preview}</SettingsHint> : null}

          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={busy || !draft.name.trim()} onClick={() => void save()}>
              {draft.id ? 'Сохранить' : 'Создать'}
            </Button>
            {draft.id ? (
              <Button type="button" variant="ghost" disabled={busy} onClick={() => setDraft(emptyDraft(anchor))}>
                Отмена
              </Button>
            ) : null}
          </div>
        </div>
      </SettingsFormCard>

      <SettingsFormCard title="Сохранённые периоды">
        {loading ? <SettingsHint>Загрузка…</SettingsHint> : null}
        {!loading && presets.length === 0 ? <SettingsHint>Периодов пока нет</SettingsHint> : null}
        <div className="flex flex-col gap-3">
          {presets.map((preset) => (
            <div key={preset.id} className="flex flex-col gap-2 border-b border-border/60 pb-3 last:border-b-0">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">
                  {preset.name}
                  {preset.id === primaryId ? ' · основной' : ''}
                </p>
                <p className="text-xs text-muted-foreground">{PERIOD_PRESET_TYPE_LABELS[preset.definition.type]}</p>
              </div>
              <p className="text-sm text-muted-foreground">{presetPreview(preset, anchor)}</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => setDraft(draftFromPreset(preset, anchor))}
                >
                  Изменить
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={busy || preset.id === primaryId}
                  onClick={() => void makePrimary(preset.id)}
                >
                  Сделать основным
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  disabled={busy}
                  onClick={() => void remove(preset.id)}
                >
                  Удалить
                </Button>
              </div>
            </div>
          ))}
        </div>
      </SettingsFormCard>
    </SettingsSubPageShell>
  );
}

function presetPreview(preset: PeriodPreset, anchor: string): string {
  try {
    const range = resolvePeriod({ type: 'preset', preset: preset.definition }, anchor);
    return formatPeriodRange(range.from, range.to);
  } catch (err) {
    return err instanceof Error ? err.message : '';
  }
}
